import { gql } from '@apollo/client';
import { Box, VStack } from '@chakra-ui/react';
import { DocumentNode } from 'graphql';
import NextLink from 'next/link';
import * as React from 'react';

import { AbbreviateRedraft } from '@/components/AbbreviateRedraft';
import { AttachmentDisplay } from '@/components/AttachmentDisplay';
import { useCommentActions } from '@/components/Post/hooks/useCommentActions';
import { usePostHref } from '@/hooks/usePostHref';
import {
  PlatformContextPreviewLink,
  useLinkPreview,
} from '@/imports/ui/components/context/PlatformContextPreviewLink';

import { SimplePost_SimplePostFragment } from './graphql-types/SimplePost_SimplePostFragment';

import { Comments } from '../Comments';
import { CreateComment, CreateComment_postFragment } from '../CreateComment';
import { ImageDisplay } from '../ImageDisplay';
import { LikeCommentFooter } from '../LikeCommentFooter';
import { PostHeader } from '../PostHeader';
import { Tags } from '../Tags';
import { Title } from '../Title';

type PostHeaderProps = React.ComponentProps<typeof PostHeader>;

export const SimplePost: React.FC<{
  simplePost: SimplePost_SimplePostFragment;
  options?: PostHeaderProps['options'] & { isExpandedByDefault?: boolean };
}> & {
  simplePostFragment: DocumentNode;
} = ({ simplePost, options }) => {
  const postHref = usePostHref(simplePost._id);

  const {
    ref,
    focusAndScrollToRef,
    expandOrFocusAndScrollToRef,
    isActive,
    setIsActive,
    ...expandProps
  } = useCommentActions({
    post: simplePost,
    isExpandedByDefault: options?.isExpandedByDefault,
  });

  const { linkPreview } = useLinkPreview({
    rawDraftContentState: simplePost.bodyContentState || undefined,
  });

  return (
    <Box p="4">
      <PostHeader post={simplePost} mb="4" options={options} />
      <NextLink href={postHref}>
        <Title>{simplePost.simplePostTitle}</Title>
      </NextLink>
      {simplePost.bodyContentState && (
        <AbbreviateRedraft
          options={{ defaultIsOpen: options?.isExpandedByDefault }}
          rawDraftContentState={simplePost.bodyContentState}
          noOfCharacters={500}
        />
      )}
      <VStack spacing="4" width="full" align="start" mt="4">
        <PlatformContextPreviewLink
          linkPreview={linkPreview}
          canEdit={false}
          onClose={() => null}
          w="full"
        />
        <ImageDisplay postEmbeddedImage={simplePost.embeddedImagesV2} />
        <AttachmentDisplay versionFiles={simplePost.versionFiles} />
        <Tags post={simplePost} width="full" />
        <LikeCommentFooter
          post={simplePost}
          width="full"
          onClickComment={focusAndScrollToRef}
          onClickCommentCount={expandOrFocusAndScrollToRef}
        />
        <Comments post={simplePost} expandProps={expandProps} />
        <CreateComment
          post={simplePost}
          ref={ref}
          isActive={isActive}
          setIsActive={setIsActive}
        />
      </VStack>
    </Box>
  );
};

SimplePost.simplePostFragment = gql`
  fragment SimplePost_SimplePostFragment on SimplePost {
    _id
    simplePostTitle: title
    bodyContentState {
      ...useLinkPreviewFragment
    }

    ...PostHeader_PostFragment
    ...Tags_PostFragment
    ...LikeCommentFooter_PostFragment
    ...Comments_PostFragment
    ...CreateComment_PostFragment
    versionFiles {
      _id
      ...AttachmentDisplay_VersionFileFragment
    }
    embeddedImagesV2 {
      ...ImageDisplay_PostEmbeddedImageFragment
    }
    ...useCommentActions_PostFragment
  }
  ${useLinkPreview.rawDraftContentStateFragment}
  ${PostHeader.postFragment}
  ${Tags.postFragment}
  ${LikeCommentFooter.postFragment}
  ${Comments.postFragment}
  ${CreateComment_postFragment}
  ${AttachmentDisplay.versionFileFragment}
  ${ImageDisplay.postEmbeddedImageFragment}
  ${useCommentActions.postFragment}
`;
